import { computed, toRef } from 'vue'
import type { FormItemProp } from 'element-plus/lib/components'
import { useSwapEmptyForm } from './useSwapEmptyForm'
import { useSwapEmptyValidation } from './useSwapEmptyValidation'
import { useSwapEmptyRules } from './useSwapEmptyRules'
import { useSwapEmptyFeatures } from './useSwapEmptyFeatures'
import { useSwapEmptyActions } from './useSwapEmptyActions'
import type { ContainerType, ShippingLine } from '~/services/apiClient'
import type { Appointment } from '~/models/appointments'
import { getTerminalLabel } from '~/constants'

interface UseSwapEmptyDialogLogicProps {
  appointment: Appointment
  showReturnRules: boolean
  shippingLine?: ShippingLine
  containerType?: ContainerType
  prefillContainerNumber?: string | null
}

export function useSwapEmptyDialogLogic(props: UseSwapEmptyDialogLogicProps) {
  // Create terminal ref and computed
  const terminal = toRef(props.appointment, 'terminal')
  const terminalLabel = computed(() => getTerminalLabel(terminal.value))

  // Initialize form composable
  const formLogic = useSwapEmptyForm({
    shippingLine: props.shippingLine,
    containerType: props.containerType,
    prefillContainerNumber: props.prefillContainerNumber,
  })

  // Initialize validation composable
  const validationLogic = useSwapEmptyValidation({
    terminal,
    form: formLogic.form,
    formRules: formLogic.formRules,
  })

  // Initialize rules composable
  const rulesLogic = useSwapEmptyRules({
    appointment: props.appointment,
    showReturnRules: props.showReturnRules,
    shippingLine: props.shippingLine,
    containerType: props.containerType,
    terminal,
    form: formLogic.form,
  })

  // Initialize features composable
  const featuresLogic = useSwapEmptyFeatures({
    appointment: props.appointment,
    showReturnRules: props.showReturnRules,
    terminal,
    form: formLogic.form,
    loadingEmptyData: validationLogic.loadingEmptyData,
    selectedShippingLine: rulesLogic.selectedShippingLine,
  })

  // Initialize actions composable
  const actionsLogic = useSwapEmptyActions({
    appointment: props.appointment,
    form: formLogic.form,
    validateForm: formLogic.validateForm,
    validateEmptyContainer: validationLogic.validateEmptyContainer,
  })

  // Create validateField wrapper that calls validation
  async function validateField(prop: FormItemProp, isValid: boolean) {
    await formLogic.validateField(
      prop,
      isValid,
      validationLogic.validateEmptyContainer
    )
  }

  return {
    // Form logic
    ...formLogic,

    // Validation logic
    ...validationLogic,

    // Rules logic
    ...rulesLogic,

    // Features logic
    ...featuresLogic,

    // Actions logic
    ...actionsLogic,

    // Computed
    terminal,
    terminalLabel,

    // Methods
    validateField,
  }
}
